import { Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { Role } from '@ankor-io/common/types/account'

import App from '@/App'
import AccountSettingsPage from '@/pages/AccountSettings'
import CardEditPage from '@/pages/CardEdit'
import CardholdersPage from '@/pages/Cardholders'
import CardsPage from '@/pages/Cards'
import CharterDetailPage from '@/pages/CharterDetail'
import CharterEditPage from '@/pages/CharterEdit'
import CharterNewPage from '@/pages/CharterNew'
import CharterSignOffPage from '@/pages/CharterSignOff'
import ChartersPage from '@/pages/Charters'
import ConnectedAccountUsersPage from '@/pages/ConnectedAccountUsers'
import DashboardPage from '@/pages/Dashboard'
import Forbidden from '@/pages/Forbidden'
import GlobalAccountDetailsPage from '@/pages/GlobalAccountDetails'
import InvitationLandingPage from '@/pages/InvitationLanding'
import KYCRFIPage from '@/pages/KYCRFI'
import MyCardsPage from '@/pages/MyCards'
import NewConversionPage from '@/pages/NewConversion'
import NewGlobalAccountPage from '@/pages/NewGlobalAccount'
import NotFound from '@/pages/NotFound'
import OnboardingPage from '@/pages/Onboarding'
import ProfilePage from '@/pages/Profile'
import SignInPage from '@/pages/SignIn'
import SignUpPage from '@/pages/SignUp'
import TransactionDetailPage from '@/pages/TransactionDetail'
import TransactionEditPage from '@/pages/TransactionEdit'
import TransactionNewPage from '@/pages/TransactionNew'
import TransactionsPage from '@/pages/Transactions'
import UsersPage from '@/pages/Users'
import ViewCardDetailsPage from '@/pages/ViewCardDetails'
import ViewCardholderDetailsPage from '@/pages/ViewCardholderDetails'
import WalletPage from '@/pages/Wallet'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import Loading from '@/ui-components/Loading'
import ActiveUsers from '@/views/Users/ActiveUsers'
import Invites from '@/views/Users/Invites'

const Router = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Auth Pages */}
          <Route
            path="/sign-in"
            element={<SignInPage />}
          />

          <Route
            path="/sign-up"
            element={<SignUpPage />}
          />

          <Route
            path="/invite/:inviteId"
            element={<InvitationLandingPage />}
          />

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={<OnboardingPage />}
          />

          <Route element={<App />}>
            <Route
              path="/forbidden"
              element={<Forbidden />}
            />

            {/* Dashboard */}
            <Route
              path="/"
              element={
                <ProtectedRoute minRole={Role.VIEWER}>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Account Settings Pages */}
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <AccountSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Cards Pages */}
            <Route
              path="/cards"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <CardsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cards/card-edit/:cardId"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <CardEditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cards/card-details/:cardId"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <ViewCardDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cards/cardholders"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <CardholdersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cards/cardholders/cardholder-details/:cardholderId"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <ViewCardholderDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-cards"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <MyCardsPage />
                </ProtectedRoute>
              }
            />

            {/* Charter Pages */}
            <Route
              path="/charters"
              element={
                <ProtectedRoute minRole={Role.VIEWER}>
                  <ChartersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/charters/:charterId"
              element={
                <ProtectedRoute minRole={Role.SPENDER}>
                  <CharterDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/charters/new"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <CharterNewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/charters/:charterId/edit"
              element={
                <ProtectedRoute minRole={Role.SPENDER}>
                  <CharterEditPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/charters/:charterId/transactions/new"
              element={
                <ProtectedRoute minRole={Role.SPENDER}>
                  <TransactionNewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/charters/:charterId/sign-off"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <CharterSignOffPage />
                </ProtectedRoute>
              }
            />

            {/* Global Level Transactions Pages */}
            <Route
              path="/transactions"
              element={
                <ProtectedRoute minRole={Role.SPENDER}>
                  <TransactionsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/transactions/:transactionId"
              element={
                <ProtectedRoute minRole={Role.SPENDER}>
                  <TransactionDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/transactions/:transactionId/edit"
              element={
                <ProtectedRoute minRole={Role.SPENDER}>
                  <TransactionEditPage />
                </ProtectedRoute>
              }
            />

            {/* Users Pages */}
            <Route
              path="/users"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <UsersPage />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Navigate
                    to="list"
                    replace
                  />
                }
              />
              <Route
                path="list"
                element={<ActiveUsers />}
              />
              <Route
                path="invites"
                element={<Invites />}
              />
            </Route>

            {/* Connected Account Users Pages */}
            <Route
              path="/vessel-users"
              element={
                <ProtectedRoute minRole={Role.APPROVER}>
                  <ConnectedAccountUsersPage />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Navigate
                    to="list"
                    replace
                  />
                }
              />
              <Route
                path="list"
                element={<ActiveUsers />}
              />
              <Route
                path="invites"
                element={<Invites />}
              />
            </Route>

            {/* Wallet Pages */}
            <Route
              path="/wallet"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <WalletPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/global-accounts/:globalAccountId"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <GlobalAccountDetailsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/global-accounts/new"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <NewGlobalAccountPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/conversions/new"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <NewConversionPage />
                </ProtectedRoute>
              }
            />

            {/* KYC Pages */}
            <Route
              path="/kyc-rfi"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <KYCRFIPage />
                </ProtectedRoute>
              }
            />

            {/* Profile Pages */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute minRole={Role.ADMIN}>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* 404 Catch-all Route - Must be last */}
            <Route
              path="*"
              element={<NotFound />}
            />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default Router
